// Общий тост на всё приложение.
//
// Зачем: в 23 местах отказ операции обрабатывался как `onError: () => haptic('error')`
// — телефон вибрировал, диалог закрывался, и всё. Неудавшаяся оплата долга
// выглядела ровно как удавшаяся. Для приложения про деньги это худший вид
// отказа: пользователь уверен, что заплатил.
//
// Локальный тост уже был в «Бюджете», но заводить такое же состояние ещё в
// девяти файлах — значит получить девять разных видов сообщения. Провайдер
// один, показывает один тост за раз (последний вытесняет предыдущий: два
// сообщения подряд всё равно не прочитать).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { haptic } from './telegram';

export type ToastTone = 'info' | 'error';

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ToastInput = {
  text: string;
  tone?: ToastTone;
  action?: ToastAction;
};

type Ctx = {
  /** Показать сообщение. Строка — то же, что `{ text }` с тоном info. */
  show: (t: ToastInput | string) => void;
  /** Отказ операции: красный тон, живёт дольше и даёт вибрацию ошибки. */
  error: (text: string, action?: ToastAction) => void;
  dismiss: () => void;
};

const ToastContext = createContext<Ctx | null>(null);

// Ошибку читают дольше, чем подтверждение; с действием — ещё дольше, иначе
// кнопка «Отменить» исчезает раньше, чем до неё дотянутся.
function lifetimeMs(t: ToastInput): number {
  if (t.action) return 7000;
  return t.tone === 'error' ? 6000 : 4000;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toast, setToast] = useState<ToastInput | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setToast(null);
  }, []);

  const show = useCallback((t: ToastInput | string) => {
    const next = typeof t === 'string' ? { text: t } : t;
    if (timer.current) clearTimeout(timer.current);
    setToast(next);
    timer.current = setTimeout(() => setToast(null), lifetimeMs(next));
  }, []);

  const error = useCallback(
    (text: string, action?: ToastAction) => {
      haptic('error');
      show({ text, tone: 'error', action });
    },
    [show],
  );

  // Таймер переживает размонтирование, если уйти с экрана сразу после действия.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const value = useMemo<Ctx>(() => ({ show, error, dismiss }), [show, error, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        // <output> уже несёт role="status": скринридер прочитает сообщение
        // сам, без ручного role на div.
        <output className={`toast ${toast.tone === 'error' ? 'is-error' : ''}`}>
          <span className="toast-text">{toast.text}</span>
          {toast.action ? (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                toast.action?.onClick();
                dismiss();
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
        </output>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): Ctx {
  const v = useContext(ToastContext);
  if (!v) {
    // Вне провайдера (тесты, изолированный рендер) молчим, но не падаем:
    // сообщение об ошибке не должно само становиться ошибкой.
    return {
      show: () => {},
      error: () => {},
      dismiss: () => {},
    };
  }
  return v;
}
